/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import RiskGaugeChart from './RiskGaugeChart';
import riskIndicatorStyles, { themeRiskIndicatorStyles } from './RiskIndicator.styles';

interface SVG {
    svgPath?: string;
    svgWidth?: string;
    svgHeight?: string;
    svgColor?: string;
}

export interface ChartOptions {
    path?: string;
    value?: string;
}

export interface Image {
    imageSrc: string;
    imageAlt: string;
}

interface RiskIndicatorProps {
    disableTabStaticData?: boolean;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, RiskIndicatorProps {
    riskIndicatorTitle: string;
    displayRatingLabel: boolean;
    riskRatingsDataKey: string;
    riskRatings: string;
    ariaLabelsRiskRatings: string;
    addTabIndexToSelectedTile: boolean;
    tileAriaLabel: string;
    isTileShapeSvg: boolean;
    svgs: SVG[];
    _data?: Object;
    typographyClass?: string;
    riskIndicatorType: 'Tile' | 'Gauge chart' | 'Images';
    showChartDashedOuterLine?: boolean;
    chartDashedLineColor?: string;
    chartOptions?: ChartOptions[];
    chartIndicatorColor?: string;
    chartBackgroundColor?: string;
    chartDialColor?: string;
    images?: Image[];
}

interface WidgetProps extends commonTypes.WidgetInstanceProps {
    _data?: object;
    tileAriaLabel?: string;
}

const getRiskIndicatorTiles = (
    selectedTile: number,
    riskRatings: string[],
    isTileShapeSvg: boolean,
    svgs: SVG[]
): JSX.Element[] => {
    const content: JSX.Element[] = [];

    for (let i = 0; i < 5; i++) {
        const selectedTileClassName: string = selectedTile === i + 1 ? 'selected' : '';
        content.push(
            <div className="tile" key={i}>
                <div
                    className={`shape shape-${i + 1} ${selectedTileClassName}`}
                    data-testid={`${selectedTileClassName}Tile`}
                >
                    <div className="color-swatch"></div>
                </div>
                <div
                    className={`scale-cell cell-${i + 1} ${selectedTileClassName}`}
                    data-testid={`cell-${i + 1}`}
                >
                    {isTileShapeSvg && Array.isArray(svgs) && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={`${svgs[i]?.svgWidth || 50}`}
                            height={`${svgs[i]?.svgHeight || 40}`}
                        >
                            <path
                                d={`${svgs[i]?.svgPath || 'M0,37.5 50,30 50,50 0,50z'}`}
                                fill={`${svgs[i]?.svgColor || 'green'}`}
                            />
                        </svg>
                    )}
                </div>
                <div className={`label label-${i + 1}`}>{riskRatings[i]}</div>
            </div>
        );
    }

    return content;
};

const RiskIndicator = (props: WidgetProps): JSX.Element => {
    const { disableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        riskIndicatorTitle,
        displayRatingLabel,
        riskRatingsDataKey,
        riskRatings,
        ariaLabelsRiskRatings,
        tileAriaLabel,
        isTileShapeSvg,
        svgs,
        ...rest
    } = widgetProperties || props;
    const { data, _data } = props;
    const i18n = commonHooks.useLocalization();

    const compiledriskIndicatorTitle = i18n.format(riskIndicatorTitle || '', data);

    let riskRatingData: any, _riskRatings: string;
    if (_data || riskRatingsDataKey) {
        riskRatingData = _data || JsonQuery(riskRatingsDataKey as string, { data });
        _riskRatings = JsonQuery(riskRatings as string, { data }).value;
    } else {
        riskRatingData = { value: { sortOrder: '002' } };
        _riskRatings = 'Low,,,,High';
    }

    const theme = data?.themeSelected || props?.theme;
    const riskImage = rest?.images?.[parseInt(riskRatingData?.value?.sortOrder) - 1];
    let ariaLabel = tileAriaLabel ? JsonQuery(tileAriaLabel as string, { data }).value : '';
    const ariaLabelRiskRatings = ariaLabelsRiskRatings
        ? JsonQuery(ariaLabelsRiskRatings as string, { data })
              .value?.toString()
              .split(',')?.[parseInt(riskRatingData.value?.sortOrder) - 1] || ''
        : '';

    if (props.tileAriaLabel && !ariaLabel) {
        ariaLabel = i18n.format(props.tileAriaLabel || '', data) || '';
    }

    return (
        <div
            className={`risk-indicator ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? riskIndicatorStyles : themeRiskIndicatorStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles}
            `}
        >
            {compiledriskIndicatorTitle && (
                <h3 className="risk-indicator-title" tabIndex={disableTabStaticData ? -1 : 0}>
                    {compiledriskIndicatorTitle}
                </h3>
            )}
            {!rest.riskIndicatorType || rest.riskIndicatorType === 'Tile' ? (
                <>
                    {displayRatingLabel && (
                        <p className="risk-rating">{riskRatingData.value?.name}</p>
                    )}
                    <div
                        className="row"
                        role="img"
                        tabIndex={rest.addTabIndexToSelectedTile ? 0 : -1}
                        aria-label={`${ariaLabel} ${ariaLabelRiskRatings}`}
                    >
                        {getRiskIndicatorTiles(
                            parseInt(riskRatingData.value?.sortOrder),
                            _riskRatings?.toString().split(',') || [],
                            isTileShapeSvg,
                            svgs
                        )}
                    </div>
                </>
            ) : rest.riskIndicatorType === 'Gauge chart' ? (
                <div className="chart-container">
                    <RiskGaugeChart
                        value={parseInt(riskRatingData?.value?.sortOrder)}
                        options={rest?.chartOptions}
                        showDashedOuterLine={rest?.showChartDashedOuterLine}
                        dashedLineColor={rest?.chartDashedLineColor}
                        indicatorColor={rest?.chartIndicatorColor}
                        backgroundColor={rest?.chartBackgroundColor}
                        dialColor={rest?.chartDialColor}
                    />
                    {displayRatingLabel && (
                        <p className="chart-risk-rating">{riskRatingData?.value?.name}</p>
                    )}
                </div>
            ) : rest.riskIndicatorType === 'Images' ? (
                <div className="risk-image-container">
                    {riskImage?.imageSrc && (
                        <img
                            className="risk-image"
                            src={riskImage.imageSrc}
                            alt={riskImage?.imageAlt || riskRatingData?.value?.name || 'risk-image'}
                        />
                    )}
                    {displayRatingLabel && (
                        <p className="chart-risk-rating">{riskRatingData?.value?.name}</p>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default RiskIndicator;
